import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  MenuItem,
  Menu,
  IconButton,
  Tooltip,
} from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import StorefrontIcon from '@mui/icons-material/Storefront';
import DataThresholdingOutlinedIcon from '@mui/icons-material/DataThresholdingOutlined';
import NotificationImportantOutlinedIcon from '@mui/icons-material/NotificationImportantOutlined';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';

import { SubNavStateContext, NavWidthContext } from '../../contexts';

import StatusBadgeDropdown from '../../pages/EnterpriseProjects/components/StatusBadgeDropdown';

import useFetch from '../../hooks/useFetch';

import { truncateStr } from '../../utils';

import './SubNav.scss';

export default function SubNav() {
  const { subNavState, setSubNavState } = useContext(SubNavStateContext);
  const { navWidth } = useContext(NavWidthContext);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [blockerStatus, setBlockerStatus] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [projectStatus, setProjectStatus] = useState(null);
  const [priority, setPriority] = useState(subNavState?.projectToView?.priority || 2);

  useEffect(() => {
    if (subNavState?.transactionToView) {
      setCurrentStatus(subNavState.transactionToView.status);
      setBlockerStatus(subNavState.transactionToView.blockerStatus);
    }
    if (subNavState?.projectToView?.priority) {
      setPriority(subNavState.projectToView.priority || 2);
    }
  }, [subNavState.projectToView, subNavState.transactionToView]);

  useEffect(() => {
    if (subNavState?.status?.updateStatus) {
      setCurrentStatus(subNavState?.status?.updateStatus);
    }
    if (subNavState?.status?.blockerStatus) {
      setBlockerStatus(subNavState?.status?.blockerStatus);
    }
  }, [subNavState]);

  const nav = useNavigate();

  const [, updatePriorityRequest] = useFetch();

  function updatePriority(value) {
    const priorityData = {
      priority: value,
      projectId: subNavState.projectToView.projectId,
      enterpriseCompanyId: subNavState.projectToView.companyId,
    };
    updatePriorityRequest({
      url: '/homepages/enterprise/update-priority',
      method: 'post',
      body: priorityData,
      bodyIds: ['requestUserId'],
      onSuccess: () => {
        setSubNavState((prevState) => ({
          ...prevState,
          updateProjectPriority: {
            projectId: subNavState.projectToView.projectId,
            priority: value,
          },
        }));
      },
    });
  }

  if (subNavState.projectToView) {
    return (
      <div className="SubNav" style={{ minWidth: navWidth }}>
        <Button
          onClick={() => {
            nav(-1);
            setSubNavState({ ...subNavState, transactionToView: null, currentPage: 'company-list' });
          }}
        >
          <ArrowBackIcon />
        </Button>
        <Button
          onClick={() => {
            if (projectStatus) setSubNavState({ ...subNavState, projectStatus });
            nav('/');
          }}
        >
          <HomeOutlinedIcon />
          Home
        </Button>
        <Button className="no-pointer-events">
          <AccountBalanceOutlinedIcon />
          {subNavState.projectToView.investorFirmName?.length > 100 ? (
            <Tooltip title={subNavState.projectToView.investorFirmName} arrow placement="bottom">
              <span className="truncated-nav-text">
                {truncateStr(subNavState.projectToView.investorFirmName, 100)}
              </span>
            </Tooltip>
          ) : (
            <span className="truncated-nav-text">
              {subNavState.projectToView.investorFirmName}
            </span>
          )}
        </Button>
        <Button
          className={`${subNavState.currentPage === 'company-list' ? 'active' : ''}`}
          onClick={() => {
            nav(`/?pId=${subNavState.projectToView.projectId}`);
            setSubNavState({
              ...subNavState,
              transactionToView: null,
              currentPage: 'company-list',
              status: {
                companyId: subNavState.transactionToView.transactionId,
                updateStatus: currentStatus,
                blockerStatus,
              },
            });
          }}
        >
          <DataThresholdingOutlinedIcon />
          {`ASC 820 | ${subNavState.projectToView.createdDate}`}
        </Button>
        {subNavState.currentPage === 'calc-inputs' && (
          <Button className="active">
            <StorefrontIcon />
            {subNavState.companyName?.length > 100 ? (
              <Tooltip title={subNavState.companyName} arrow placement="bottom">
                <span className="truncated-nav-text">
                  {truncateStr(subNavState.companyName, 100)}
                </span>
              </Tooltip>
            ) : (
              <span className="truncated-nav-text">
                {subNavState.companyName}
              </span>
            )}
          </Button>
        )}
        <div className="top-nav-right">
          {subNavState.transactionToView && (
            <StatusBadgeDropdown
              setInsideElementHover={() => { }}
              company={subNavState?.transactionToView}
              setProjectCompanies={() => { }}
              projectCompanies={[]}
              currentStatus={currentStatus}
              setCurrentStatus={setCurrentStatus}
              blockerStatus={blockerStatus}
              setBlockerStatus={setBlockerStatus}
              setProjectStatus={setProjectStatus}
              subNav
            />
          )}
          <div className="priority-wrapper">
            <IconButton
              className="priority-button"
              onClick={(e) => {
                setMenuOpen(!menuOpen);
                setAnchorEl(e.currentTarget);
              }}
            >
              {priority === '1' ?
                <NotificationImportantOutlinedIcon className="high-icon" /> :
                priority === '2' ?
                  'Priority' :
                  <ArrowDownwardIcon className="low-icon" />}
              <KeyboardArrowDownOutlinedIcon sx={{ marginRight: '2px' }} />
            </IconButton>
            <Menu
              id="priority-menu"
              anchorEl={anchorEl}
              value={priority || ''}
              open={menuOpen}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
              onClose={() => setMenuOpen(!menuOpen)}
              onChange={(e) => { updatePriority(e.target.value); }}
            >
              <MenuItem
                className={priority === '1' ? 'active' : ''}
                onClick={() => {
                  setPriority('1');
                  setMenuOpen(!menuOpen);
                  updatePriority('1');
                }}
              >
                <NotificationImportantOutlinedIcon className="high-icon" />
                High
              </MenuItem>
              <MenuItem
                className={priority === '2' ? 'active' : ''}
                onClick={() => {
                  setPriority('2');
                  setMenuOpen(!menuOpen);
                  updatePriority('2');
                }}
              >
                <ArrowDownwardIcon sx={{ visibility: 'hidden', marginRight: '2px' }} />
                Normal
              </MenuItem>
              <MenuItem
                className={priority === '3' ? 'active' : ''}
                onClick={() => {
                  setPriority('3');
                  setMenuOpen(!menuOpen);
                  updatePriority('3');
                }}
              >
                <ArrowDownwardIcon className="low-icon" />
                Low
              </MenuItem>
            </Menu>
          </div>
        </div>
      </div>
    );
  }
}
